import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { customerApi, money, fmtDate, statusPill, statusLabel, API } from '../../lib/api';

export default function CustomerInvoiceDetail({ me }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [payAmt, setPayAmt] = useState(0);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = () => customerApi.get(`/customer/invoices/${id}`).then((r) => { setInv(r.data); setPayAmt(r.data.total - r.data.paid_amount); });
  useEffect(() => { load(); }, [id]);

  // Handle Stripe return
  useEffect(() => {
    const session_id = searchParams.get('session_id');
    if (session_id && inv && !polling) {
      setPolling(true);
      const poll = async (attempt = 0) => {
        if (attempt >= 10) { setPolling(false); toast.error('Payment status check timed out'); return; }
        try {
          const r = await customerApi.get(`/payments/status/${session_id}`);
          if (r.data.payment_status === 'paid') { toast.success('Payment successful!'); setPolling(false); searchParams.delete('session_id'); setSearchParams(searchParams); load(); return; }
          if (r.data.status === 'expired') { setPolling(false); toast.error('Payment expired'); return; }
        } catch {}
        setTimeout(() => poll(attempt + 1), 2000);
      };
      poll();
    }
  }, [searchParams, inv]);

  if (!inv) return <div className="py-20 text-center text-sm text-[#6b7280]">Loading...</div>;
  const balance = inv.total - inv.paid_amount;

  const payNow = async () => {
    setPaying(true);
    try {
      const amt = me?.allow_partial_payment ? Number(payAmt) : balance;
      const r = await customerApi.post(`/payments/pay-invoice/${id}`, { amount: amt, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Payment failed'); setPaying(false); }
  };

  const download = async () => {
    const resp = await fetch(`${API}/customer/invoices/${id}/pdf`, { credentials: 'include' });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fade-in space-y-5 max-w-3xl mx-auto" data-testid="cust-invoice-detail">
      <button onClick={() => navigate('/portal/invoices')} className="text-sm text-[#6b7280] flex items-center gap-1"><ArrowLeft size={16} /> Back</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap"><h1 className="text-3xl font-semibold mono">{inv.invoice_number}</h1><span className={statusPill(inv.status)}>{statusLabel(inv.status)}</span></div>
          <div className="text-sm text-[#6b7280] mt-1">Issued {fmtDate(inv.issue_date)} · Due {fmtDate(inv.due_date)}</div>
        </div>
        <button onClick={download} className="btn-ghost flex items-center gap-2" data-testid="cust-download-pdf"><Download size={14} /> PDF</button>
      </div>

      {balance > 0 && (
        <div className="card p-6 bg-gradient-to-br from-[#fde8e8] to-white border-[#fde8e8]">
          <div className="text-xs font-semibold text-[#d92027] uppercase tracking-wider mb-1">Balance Due</div>
          <div className="text-4xl sm:text-5xl font-semibold mono text-[#d92027] mb-4">{money(balance)}</div>

          {me?.allow_partial_payment && balance > 0 && (
            <div className="mb-4">
              <label className="text-xs text-[#6b7280] uppercase tracking-wide font-semibold">Pay amount</label>
              <input type="range" min="1" max={balance} step="1" value={payAmt} onChange={(e) => setPayAmt(Number(e.target.value))} className="w-full accent-[#d92027]" data-testid="partial-pay-slider" />
              <div className="flex justify-between text-xs text-[#6b7280]"><span>$1</span><span className="font-semibold mono text-[#0a0a0a]">{money(payAmt)}</span><span>{money(balance)}</span></div>
              <input type="number" step="0.01" min="1" max={balance} value={payAmt} onChange={(e) => setPayAmt(Number(e.target.value))} className="mt-2 w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm mono font-semibold" data-testid="partial-pay-input" />
            </div>
          )}

          <button onClick={payNow} disabled={paying || polling} className="w-full btn-brand flex items-center justify-center gap-2 disabled:opacity-50" data-testid="pay-now-btn">
            <CreditCard size={16} />
            {paying ? 'Redirecting…' : polling ? 'Confirming payment…' : `Make a payment — ${money(me?.allow_partial_payment ? payAmt : balance)}`}
          </button>
          <p className="text-[11px] text-[#6b7280] mt-2 text-center">
            Secure checkout · Apple Pay, Google Pay, Card, Cash App, Link
          </p>
        </div>
      )}

      <div className="card overflow-hidden">
        {/* Desktop table */}
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-2">Item</th><th className="text-right px-4 py-2">Qty</th><th className="text-right px-4 py-2">Price</th><th className="text-right px-4 py-2">Total</th></tr>
          </thead>
          <tbody>
            {(inv.items || []).map((it, i) => (
              <tr key={i} className="border-t border-[#f3f3f3]">
                <td className="px-4 py-3 font-medium">{it.name}</td>
                <td className="text-right px-4 py-3 mono">{it.qty}</td>
                <td className="text-right px-4 py-3 mono">{money(it.unit_price)}</td>
                <td className="text-right px-4 py-3 mono font-semibold">{money(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#0a0a0a]"><td colSpan={3} className="px-4 py-3 text-right font-semibold">Total</td><td className="text-right px-4 py-3 mono font-bold text-[#d92027] text-lg">{money(inv.total)}</td></tr>
            {inv.paid_amount > 0 && <tr className="border-t border-[#f3f3f3]"><td colSpan={3} className="px-4 py-2 text-right text-xs text-[#6b7280]">Paid</td><td className="text-right px-4 py-2 mono text-green-700">{money(inv.paid_amount)}</td></tr>}
          </tfoot>
        </table>
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-[#f3f3f3]">
          {(inv.items || []).map((it, i) => (
            <div key={i} className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">{it.name}</div>
                <div className="text-xs text-[#6b7280] mono mt-0.5">Qty {it.qty} × {money(it.unit_price)}</div>
              </div>
              <div className="mono font-semibold text-sm shrink-0">{money(it.line_total)}</div>
            </div>
          ))}
          <div className="p-4 bg-[#fafafa] flex items-center justify-between">
            <span className="text-sm font-semibold">Total</span>
            <span className="mono font-bold text-[#d92027] text-lg">{money(inv.total)}</span>
          </div>
          {inv.paid_amount > 0 && (
            <div className="p-3 flex items-center justify-between text-xs">
              <span className="text-[#6b7280]">Paid</span>
              <span className="mono text-green-700 font-semibold">{money(inv.paid_amount)}</span>
            </div>
          )}
        </div>
      </div>

      {inv.payments?.length > 0 && (
        <div className="card p-4">
          <div className="text-xs font-semibold text-[#6b7280] uppercase mb-2">Payment History</div>
          {inv.payments.map((p) => (
            <div key={p.payment_id} className="flex justify-between py-2 border-b border-[#f3f3f3] last:border-0 text-sm">
              <span>{p.method} · {fmtDate(p.created_at)}</span>
              <span className="mono font-semibold text-green-700">{money(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
